import React, { useState } from 'react';
import { MailIcon, MapPinIcon, LinkedinIcon, CheckCircle2Icon } from 'lucide-react';
import { PageShell } from '../components/PageShell';
import { SectionHeading } from '../components/SectionHeading';
import { inquiryTypes, profile } from '../data/profile';
export function ContactPortal() {
  const [submitted, setSubmitted] = useState(false);
  const [name, setName] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [email, setEmail] = useState('');
  const [inquiryType, setInquiryType] = useState(inquiryTypes[0]);
  const [message, setMessage] = useState('');
  const inputClass = 'w-full border border-platinum-300 bg-white px-4 py-3 font-sans text-sm text-navy-900 outline-none transition-colors duration-150 ease-out placeholder:text-charcoal-600/60 focus:border-gold-600';
  const labelClass = 'block font-sans text-xs font-semibold uppercase tracking-[0.14em] text-charcoal-600';
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const subject = `Board inquiry: ${inquiryType} from ${organisation}`;
    const body = [`Name: ${name}`, `Organisation: ${organisation}`, `Email: ${email}`, `Nature of enquiry: ${inquiryType}`, '', 'Details:', message].join('\n');
    window.location.href = `mailto:${profile.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSubmitted(true);
  };
  return <PageShell path="/contact" data-id="element-270" data-mp="32gwk10qur9f7">
      <section className="bg-white py-20 lg:py-24" aria-labelledby="contact-heading" data-id="element-271" data-mp="32h6a0obwdgkw">
        <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-14 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-272" data-mp="2ohnh32rnqbt6">
          <div id="direct-contact" className="scroll-mt-24 lg:col-span-5" data-id="element-273" data-mp="2tgi0a5tje2dt">
            <div id="contact-heading" data-id="element-274" data-mp="mozacjft168n">
              <SectionHeading eyebrow="Direct Contact" title="Reach Yolandi directly" data-id="element-275" data-mp="mq1wke26mki8" />
            </div>

            <dl className="mt-10 divide-y divide-platinum-200 border-y border-platinum-200" data-id="element-276" data-mp="rm960gtsee4x">
              <div className="flex items-center gap-4 py-5" data-id="element-277" data-mp="q8hiff15r6ex">
                <MailIcon className="h-5 w-5 shrink-0 text-gold-600" aria-hidden="true" data-id="element-278" data-mp="l71yjqapv5y4" />
                <div className="min-w-0" data-id="element-279" data-mp="g877k12lib2x">
                  <dt className={labelClass} data-id="element-280" data-mp="g98ub2sz4m0h">Email</dt>
                  <dd className="mt-1 truncate font-sans text-base font-medium text-navy-900" data-id="element-281" data-mp="bap7l06k3672">
                    <a href={`mailto:${profile.email}`} className="transition-colors duration-150 ease-out hover:text-gold-600" data-id="element-282" data-mp="18xxmudfyrj0m">
                      {profile.email}
                    </a>
                  </dd>
                </div>
              </div>
              <div className="flex items-center gap-4 py-5" data-id="element-283" data-mp="u7qbehodj09f">
                <MapPinIcon className="h-5 w-5 shrink-0 text-gold-600" aria-hidden="true" data-id="element-284" data-mp="1rnvuisfuna3q" />
                <div data-id="element-285" data-mp="1mxfincrofwjh">
                  <dt className={labelClass} data-id="element-286" data-mp="1mwxezv2oqg1j">Based in</dt>
                  <dd className="mt-1 font-sans text-base font-medium text-navy-900" data-id="element-287" data-mp="1mvcjo38jqt2m">
                    {profile.location}
                  </dd>
                </div>
              </div>
            </dl>

            <a href={profile.linkedIn} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center justify-center gap-2.5 whitespace-nowrap bg-navy-900 px-6 py-3.5 font-sans text-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-navy-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold-600" data-id="element-288" data-mp="1z9htnl0cunge">
              <LinkedinIcon className="h-4 w-4" aria-hidden="true" data-id="element-289" data-mp="2mxsr0e6ue4zx" />
              Connect on LinkedIn
            </a>

            <p className="mt-10 border-l-2 border-gold-500 pl-5 font-sans text-base leading-relaxed text-charcoal-700" data-id="element-290" data-mp="2rygdqno1fzoc">
              {profile.disclaimer}
            </p>
          </div>

          <div id="inquiry" className="scroll-mt-24 lg:col-span-7" data-id="element-291" data-mp="31urtjnmu77nj">
            <div className="border border-platinum-200 bg-platinum-100 p-8 lg:p-10" data-id="element-292" data-mp="2fh9jqd2vzj4">
              <h2 className="font-display text-xl font-bold tracking-tight text-navy-900" data-id="element-293" data-mp="3tnburo6v7vhi">
                Board inquiry
              </h2>
              <p className="mt-3 font-sans text-sm leading-relaxed text-charcoal-600" data-id="element-294" data-mp="c5smu3injl2o">
                Submit a confidential enquiry regarding a directorship, advisory mandate or executive
                appointment. Submitting composes the enquiry as an email addressed directly to{' '}
                {profile.email}, and responses are typically provided within two business days.
              </p>

              {submitted ? <div role="status" className="mt-8 flex items-start gap-4 border border-gold-500 bg-white p-6" data-id="element-295" data-mp="3km5ftjjv6sqd">
                  <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-gold-600" aria-hidden="true" data-id="element-296" data-mp="13voty96dn0s6" />
                  <div data-id="element-297" data-mp="zxzh8x4thhzg">
                    <p className="font-display text-base font-bold text-navy-900" data-id="element-298" data-mp="17ffj78uvysfg">Inquiry prepared</p>
                    <p className="mt-2 font-sans text-sm leading-relaxed text-charcoal-600" data-id="element-299" data-mp="1ceililjhekjq">
                      Thank you, {name || 'and welcome'}. Your enquiry has been addressed to{' '}
                      {profile.email} in your email client. Send it there and a response will follow
                      to the address provided.
                    </p>
                    <button type="button" onClick={() => setSubmitted(false)} className="mt-4 font-sans text-sm font-semibold text-gold-600 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-navy-900" data-id="element-300" data-mp="1q6p40ea3x3mc">
                      Submit another enquiry
                    </button>
                  </div>
                </div> : <form className="mt-8 space-y-6" onSubmit={handleSubmit} data-id="element-301" data-mp="2dvrk3hp3eoeq">
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2" data-id="element-302" data-mp="2nrhx227rj79f">
                    <div data-id="element-303" data-mp="2npdx1vczisef">
                      <label className={labelClass} htmlFor="inquiry-name" data-id="element-304" data-mp="2isn6ge3k0eer">
                        Full name
                      </label>
                      <input id="inquiry-name" name="name" required value={name} onChange={event => setName(event.target.value)} className={`mt-2 ${inputClass}`} placeholder="Jane Director" data-id="element-305" data-mp="2eph3pgfnh5d1" />
                    </div>
                    <div data-id="element-306" data-mp="1v3cdhisg3ob6">
                      <label className={labelClass} htmlFor="inquiry-organisation" data-id="element-307" data-mp="1v0q3lci30wps">
                        Organisation
                      </label>
                      <input id="inquiry-organisation" name="organisation" required value={organisation} onChange={event => setOrganisation(event.target.value)} className={`mt-2 ${inputClass}`} placeholder="Board or company name" data-id="element-308" data-mp="1m133qv503yyu" />
                    </div>
                  </div>

                  <div data-id="element-309" data-mp="akib865kd3tv">
                    <label className={labelClass} htmlFor="inquiry-email" data-id="element-310" data-mp="ajqrptp8qwjy">
                      Email address
                    </label>
                    <input id="inquiry-email" name="email" type="email" required value={email} onChange={event => setEmail(event.target.value)} className={`mt-2 ${inputClass}`} placeholder="name@organisation.com" data-id="element-311" data-mp="39xsseavbiktf" />
                  </div>

                  <div data-id="element-312" data-mp="2var6fvg9r5yh">
                    <label className={labelClass} htmlFor="inquiry-type" data-id="element-313" data-mp="2v9xzouhb8ggc">
                      Nature of enquiry
                    </label>
                    <select id="inquiry-type" name="inquiryType" value={inquiryType} onChange={event => setInquiryType(event.target.value)} className={`mt-2 ${inputClass}`} data-id="element-314" data-mp="1oprmcaxa2nnf">
                      {inquiryTypes.map(type => <option key={type} value={type} data-id="element-315" data-mp="27l88329ywr8t">
                          {type}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-316" data-mp="2hb16t2p0hbvn">
                    <label className={labelClass} htmlFor="inquiry-message" data-id="element-317" data-mp="2hbknkg2xdu83">
                      Details
                    </label>
                    <textarea id="inquiry-message" name="message" required rows={5} value={message} onChange={event => setMessage(event.target.value)} className={`mt-2 resize-y ${inputClass}`} placeholder="Mandate, timing and any relevant context." data-id="element-318" data-mp="2qcr953kflv87" />
                  </div>

                  <button type="submit" className="inline-flex w-full items-center justify-center whitespace-nowrap bg-gold-500 px-6 py-3.5 font-sans text-sm font-semibold text-navy-950 transition-colors duration-150 ease-out hover:bg-gold-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold-600 sm:w-auto" data-id="element-319" data-mp="2w15l0690yw2x">
                    Submit board inquiry
                  </button>
                </form>}
            </div>
          </div>
        </div>
      </section>
    </PageShell>;
}